// Activity Panel Web Component
import ProtectedComponent from './protected-component.js';
import { escapeHtml } from '../utils/html-utils.js';
import { formatThreadLabelHtml } from '../utils/group-threads.js';
import { buildGroupCommentUrl, buildGroupPostUrl } from '../utils/group-permalink.js';

class ActivityPanel extends ProtectedComponent {
  constructor() {
    super();
    this.activities = [];
    this.isLoading = false;
    this.currentPage = 1;
    this.hasMore = true;
    this.isExpanded = false;
    this.isWideScreen = window.innerWidth >= 1024;
    this.currentUser = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.attachEventListeners();
    this.setupResponsiveHandling();
  }

  render() {
    // Always show the component, but control content visibility via auth
    this.style.display = 'block';

    this.innerHTML = `
      <div class="activity-panel-container">
        <!-- Mobile drag handle - positioned independently -->
        <div class="drag-handle" id="dragHandle" style="display: ${this.isAuthenticated() ? 'flex' : 'none'};">
          <div class="lightning-bolt">
            ⚡
          </div>
          <div class="drag-indicator"></div>
        </div>
        
        <!-- Activity panel -->
        <div class="activity-panel ${this.isWideScreen ? 'always-visible' : ''}" id="activityPanel">
          <div class="panel-content" id="panelContent" style="display: none;">
            <div class="panel-header">
              <h3>⚡ Friend Activity</h3>
              <button class="close-panel-btn" id="closePanelBtn" title="Close">&times;</button>
            </div>
            
            <div class="activities-container" id="activitiesContainer">
              <div class="loading-state" id="loadingState">
                <div class="spinner"></div>
                <div>Loading activities...</div>
              </div>
              
              <div class="activities-list" id="activitiesList" style="display: none;">
                <!-- Activities will be loaded here -->
              </div>
              
              <div class="load-more-container" id="loadMoreContainer" style="display: none;">
                <button class="load-more-btn" id="loadMoreBtn">
                  ⚡ Load More Activities
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Overlay for mobile blur effect -->
        <div class="activity-overlay" id="activityOverlay"></div>
      </div>
    `;
  }

  attachEventListeners() {
    const dragHandle = this.querySelector('#dragHandle');
    const closePanelBtn = this.querySelector('#closePanelBtn');
    const loadMoreBtn = this.querySelector('#loadMoreBtn');
    const activitiesContainer = this.querySelector('#activitiesContainer');
    const activityOverlay = this.querySelector('#activityOverlay');

    // Toggle handle for mobile - simple click to open/close
    if (dragHandle) {
      dragHandle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.togglePanel();
      });
    }

    // Close button
    closePanelBtn.addEventListener('click', () => {
      this.closePanel();
    });

    // Overlay click to close panel
    if (activityOverlay) {
      activityOverlay.addEventListener('click', () => {
        this.closePanel();
      });
    }

    // Load more button
    loadMoreBtn.addEventListener('click', () => {
      this.loadMoreActivities();
    });

    // Infinite scroll
    activitiesContainer.addEventListener('scroll', () => {
      this.handleScroll();
    });

    // Activity item clicks
    this.addEventListener('click', (e) => {
      const activityLink = e.target.closest('.activity-link');
      if (activityLink) {
        e.preventDefault(); // Prevent default link behavior

        const href = activityLink.getAttribute('href');
        if (href && href !== '#') {
          // Use client-side router for navigation
          if (window.router) {
            window.router.navigate(href);
          } else {
            // Fallback to regular navigation
            window.location.href = href;
          }
        }

        // Close panel on mobile after navigation
        if (!this.isWideScreen) {
          setTimeout(() => this.closePanel(), 100);
        }
      }
    });
  }

  setupResponsiveHandling() {
    const handleResize = () => {
      const wasWideScreen = this.isWideScreen;
      this.isWideScreen = window.innerWidth >= 1024;

      if (wasWideScreen !== this.isWideScreen) {
        const panel = this.querySelector('.activity-panel');

        if (this.isWideScreen) {
          // Switch to always-visible mode
          panel.classList.add('always-visible');
          panel.classList.remove('expanded');
          this.isExpanded = false;
        } else {
          // Switch to mobile mode
          panel.classList.remove('always-visible');
          if (this.isExpanded) {
            panel.classList.add('expanded');
          }
        }
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
  }

  showPanel() {
    const panelContent = this.querySelector('#panelContent');
    const dragHandle = this.querySelector('#dragHandle');

    if (panelContent) {
      panelContent.style.display = 'flex';
    }

    // Show drag handle on mobile when authenticated
    if (dragHandle && !this.isWideScreen) {
      dragHandle.style.display = 'flex';
    }

    // Add authenticated class to body for CSS styling
    document.body.classList.add('authenticated');

    // Load activities when panel is shown
  }

  hidePanel() {
    const panelContent = this.querySelector('#panelContent');
    const dragHandle = this.querySelector('#dragHandle');

    if (panelContent) {
      panelContent.style.display = 'none';
    }

    // Hide drag handle when not authenticated
    if (dragHandle) {
      dragHandle.style.display = 'none';
    }

    // Remove authenticated class from body
    document.body.classList.remove('authenticated');

    this.closePanel();
  }

  togglePanel() {
    if (this.isWideScreen) return;

    if (this.isExpanded) {
      this.closePanel();
    } else {
      this.expandPanel();
    }
  }

  expandPanel() {
    if (this.isWideScreen) return;

    const panel = this.querySelector('.activity-panel');
    const dragHandle = this.querySelector('#dragHandle');
    const overlay = this.querySelector('#activityOverlay');

    panel.classList.add('expanded');
    if (dragHandle) {
      dragHandle.classList.add('expanded');
    }
    if (overlay) {
      overlay.classList.add('visible');
    }

    // Prevent body scrolling on mobile
    document.body.style.overflow = 'hidden';

    this.isExpanded = true;
  }

  closePanel() {
    if (this.isWideScreen) return;

    const panel = this.querySelector('.activity-panel');
    const dragHandle = this.querySelector('#dragHandle');
    const overlay = this.querySelector('#activityOverlay');

    panel.classList.remove('expanded');
    if (dragHandle) {
      dragHandle.classList.remove('expanded');
    }
    if (overlay) {
      overlay.classList.remove('visible');
    }

    // Restore body scrolling
    document.body.style.overflow = '';

    this.isExpanded = false;
  }

  setCurrentUser(user) {
    super.setCurrentUser(user);
    if (user) {
      this.showPanel();
      return;
    }
    this.hidePanel();
  }

  async load() {
    if (this.isLoading) return;

    this.isLoading = true;
    const loadingState = this.querySelector('#loadingState');
    const activitiesList = this.querySelector('#activitiesList');

    try {
      const response = await window.API.activities.getFriends(1, 20);

      this.activities = response.activities || [];
      this.hasMore = response.hasMore || false;
      this.currentPage = 1;

      this.renderActivities();
      this.updateLoadMoreButton();

      if (loadingState) loadingState.style.display = 'none';
      if (activitiesList) activitiesList.style.display = 'block';

    } catch (error) {
      window.logger.error('Failed to load activities:', error);
      this.renderError();
    } finally {
      this.isLoading = false;
    }
  }

  async loadMoreActivities() {
    if (this.isLoading || !this.hasMore) return;

    this.isLoading = true;
    const loadMoreBtn = this.querySelector('#loadMoreBtn');
    loadMoreBtn.disabled = true;
    loadMoreBtn.textContent = '⏳ Loading...';

    try {
      const response = await window.API.activities.getFriends(this.currentPage + 1, 20);
      const newActivities = response.activities || [];

      this.activities = [...this.activities, ...newActivities];
      this.hasMore = response.hasMore || false;
      this.currentPage++;

      this.renderActivities();
      this.updateLoadMoreButton();

    } catch (error) {
      window.logger.error('Failed to load more activities:', error);
    } finally {
      this.isLoading = false;
      loadMoreBtn.disabled = false;
      loadMoreBtn.textContent = '⚡ Load More Activities';
    }
  }

  handleScroll() {
    // Use requestAnimationFrame to batch layout reads and avoid forced reflows
    requestAnimationFrame(() => {
      const container = this.querySelector('#activitiesContainer');
      if (!container) return;

      const threshold = 100; // pixels from bottom
      // Batch all layout property reads
      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;

      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        if (this.hasMore && !this.isLoading) {
          this.loadMoreActivities();
        }
      }
    });
  }

  renderActivities() {
    const activitiesList = this.querySelector('#activitiesList');

    if (!activitiesList) {
      window.logger.error('activitiesList element not found');
      return;
    }

    if (this.activities.length === 0) {
      activitiesList.innerHTML = `
        <div class="empty-state">
          <div class="empty-icon">⚡</div>
          <h4>No friend activity yet</h4>
          <p>When your friends post or comment, you'll see their activity here!</p>
        </div>
      `;
      return;
    }

    activitiesList.innerHTML = this.activities.map(activity => this.renderActivity(activity)).join('');
  }

  renderActivity(activity) {
    const timeAgo = this.formatTimeAgo(new Date(activity.createdAt));
    const activityIcon = this.getActivityIcon(activity.type);
    const activityText = this.getActivityText(activity);
    const activityUser = this.getActivityUser(activity);
    const targetLink = this.getTargetLink(activity);
    const isGroupActivity = activity.type === 'group_post_created' || activity.type === 'group_comment_created';
    const targetBody = isGroupActivity
      ? this.renderGroupActivityTarget(activity)
      : this.renderDefaultActivityTarget(activity);

    return `
      <div class="activity-item">
        <div class="activity-header">
          <span class="activity-icon">${activityIcon}</span>
          <span class="activity-user">${activityUser}</span>
          <span class="activity-action">${activityText}</span>
        </div>
          <div class="activity-target">
            <a href="${targetLink}" class="activity-link">
              ${targetBody}
            </a>
          </div>
        <div class="activity-time">${timeAgo}</div>
      </div>
    `;
  }

  renderDefaultActivityTarget(activity) {
    const activityTitle = this.getActivityTitle(activity);
    return `
      <div class="activity-target-title">${activityTitle}</div>
      ${activity.target.postType === 'mood' && activity.target.moodStatus ? `
        <div class="activity-target-content mood-activity">
          - ${escapeHtml(activity.target.moodStatus)}
        </div>
      ` : activity.target.content ? `
        <div class="activity-target-content">${escapeHtml(activity.target.content)}</div>
      ` : ''}
    `;
  }

  renderGroupActivityTarget(activity) {
    const groupName = escapeHtml(activity.target.groupName || activity.target.title || '');
    const threadName = activity.target.threadName;
    const postPreview = activity.target.postPreview;
    const content = activity.target.content;

    let contextTitle = '';
    if (threadName) {
      contextTitle = `<div class="activity-target-title">${formatThreadLabelHtml(threadName)}</div>`;
    } else if (postPreview && activity.type === 'group_comment_created') {
      contextTitle = `<div class="activity-target-title activity-target-post-preview">${escapeHtml(postPreview)}</div>`;
    }

    return `
      ${groupName ? `<div class="activity-target-group">${groupName}</div>` : ''}
      ${contextTitle}
      ${activity.type === 'group_post_created' && content ? `
        <div class="activity-target-content">${escapeHtml(content)}</div>
      ` : ''}
      ${activity.type === 'group_comment_created' && content ? `
        <div class="activity-target-content activity-target-comment">${escapeHtml(content)}</div>
      ` : ''}
    `;
  }

  getActivityIcon(type) {
    switch (type) {
    case 'post_created': return '📝';
    case 'comment_created': return '💬';
    case 'group_post_created': return '📝';
    case 'group_comment_created': return '💬';
    case 'friend_request_sent': return '👥';
    case 'friend_request_accepted': return '👥';
    case 'invitation_accepted': return '👥';
    default: return '⚡';
    }
  }

  getActivityText(activity) {
    switch (activity.type) {
    case 'post_created':
      if (activity.target.postType === 'mood') {
        return `mood ${activity.target.moodIcon}`;
      }
      return 'posted';
    case 'comment_created': return 'commented';
    case 'group_post_created': return 'posted in';
    case 'group_comment_created': return 'commented in';
    case 'friend_request_accepted': return 'friended';
    case 'invitation_accepted': return 'friended';
    case 'friend_request_sent': return '';
    default: return 'did something';
    }
  }

  getActivityUser(activity) {
    return `<user-mention username="${escapeHtml(activity.user.username)}" mentioned-user-id="${escapeHtml(activity.user.id)}" user-id="${this.currentUser.id}"></user-mention>`;
  }

  getActivityTitle(activity) {
    switch (activity.type) {
    case 'invitation_accepted':
    case 'friend_request_sent':
    case 'friend_request_accepted': {
      if (activity.user.id === this.currentUser.id) {
        // this happens when it's an email invitation
        return escapeHtml(activity.target.title);
      }
      return `<user-mention username="${escapeHtml(activity.target.title)}" real-username="${escapeHtml(activity.target.title)}" user-id="${this.currentUser.id}"></user-mention>`;
    }
    default: return escapeHtml(activity.target.title);
    }
  }

  getTargetLink(activity) {
    switch (activity.type) {
    case 'post_created':
      return `/post/${activity.target.id}`;
    case 'comment_created':
      return `/post/${activity.target.postId}/comment/${activity.target.id}`;
    case 'group_post_created': {
      const groupId = activity.target.groupId;
      const postId = activity.target.groupPostId;
      if (!groupId || !postId) {
        return '/groups';
      }
      return buildGroupPostUrl({ groupId, postId });
    }
    case 'group_comment_created': {
      const groupId = activity.target.groupId;
      const postId = activity.target.groupPostId;
      const commentId = activity.target.id;
      if (!groupId || !postId || !commentId) {
        return '/groups';
      }
      return buildGroupCommentUrl({ groupId, postId, commentId });
    }
    case 'friend_request_accepted':
    case 'friend_added':
      return `/profile/${activity.target.username}`;
    case 'invitation_accepted':
    case 'friend_request_sent':
      return '/friends?tab=requests';
    default:
      return '/';
    }
  }

  updateLoadMoreButton() {
    const loadMoreContainer = this.querySelector('#loadMoreContainer');
    loadMoreContainer.style.display = this.hasMore ? 'block' : 'none';
  }

  renderError() {
    const activitiesList = this.querySelector('#activitiesList');
    activitiesList.innerHTML = `
      <div class="error-state">
        <div class="error-icon">😞</div>
        <h4>Failed to load activities</h4>
        <p>There was an error loading friend activities. Please refresh to try again.</p>
      </div>
    `;

    const loadingState = this.querySelector('#loadingState');
    loadingState.style.display = 'none';
    activitiesList.style.display = 'block';
  }

  formatTimeAgo(date) {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'just now';

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  }

}

// Register the custom element
customElements.define('activity-panel', ActivityPanel);
